import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import { tracked } from "@glimmer/tracking"
import { fibonacciBackoff } from "cc-frontend/services/socket"
import { Resource } from "ember-could-get-used-to-this"
import { forEach, isNil } from "lodash-es"
import { TrackedArray } from "tracked-built-ins"

export type LiveQueryName =
  | "planbooks_for_members_not_in_teams_for_school_year"
  | "members_not_in_teams"
  | "invitable_users_for_group"
  | "actions_for_session"
  | "planbooks_for_groups_and_school_year"
  | "invitable_groups_for_user"
  | "planbooks_for_user"
  | "invites_to_schools"

interface Args {
  positional: [
    queryName: LiveQueryName,
    queryId: string | null,
    offset?: number | null,
    limit?: number | null
  ]
}

export interface LiveQueryResult {
  type: string
  id: string
  offset: number
}

export default class LiveQueryResource extends Resource<Args["positional"]> {
  @service query

  @tracked results: Array<LiveQueryResult> = new TrackedArray([]) as Array<LiveQueryResult>

  @tracked isFetching = false
  @tracked nextOffset = null

  queryName: string | null = null
  queryId: string | null = null
  lastOffset = 0
  limit = 200
  queryAttempts = 0

  setup() {
    if (this.args.positional) {
      this.queryName = this.args.positional[0]
      this.queryId = this.args.positional[1]
      this.lastOffset = this.args.positional[2] || this.lastOffset
      this.limit = this.args.positional[3] || this.limit
    }

    this._fetchQuery()
  }

  declare _generatedQuery

  get generatedQuery() {
    if (isNil(this._generatedQuery)) {
      this._generatedQuery = this.query.generateLiveQuery(this.queryName, this.queryId, () => {
        this._fetchQuery()
      })
    }
    return this._generatedQuery
  }

  private _fetchQuery() {
    if (isNil(this.queryName) || isNil(this.queryId)) return false
    this.isFetching = true
    return this.query
      .fetch(this.generatedQuery, this.lastOffset, this.limit)
      .then((response) => {
        if (response.data === undefined) return

        let meta = response.meta || {}
        if (response.data.results.length === 0) {
          this.results = new TrackedArray([]) as Array<LiveQueryResult>
        } else {
          // Set the whole array to the new results
          if (this.lastOffset === 0) {
            this.results = new TrackedArray(response.data.results) as Array<LiveQueryResult>
          } else {
            // append to the existing array
            forEach(response.data.results, (result) => {
              this.results[result.offset] = result
            })
          }
        }

        this.nextOffset = meta.nextOffset
        this.isFetching = false
      })
      .catch((error) => {
        this.isFetching = false
        this.queryAttempts++
        let newDelay = fibonacciBackoff(this.queryAttempts, 1000, 60000)
        console.error(
          `FAILED_QUERY #${this.queryAttempts}: ${this.generatedQuery.type}:${this.generatedQuery.id}`,
          error
        )
        later(() => this._fetchQuery(), newDelay)
      })
  }

  update() {
    // The entire query changes
    if (this.args.positional) {
      if (this.queryName !== this.args.positional[0] || this.queryId !== this.args.positional[1]) {
        // we need to refetch everything
        this.queryName = this.args.positional[0]
        this.queryId = this.args.positional[1]
        this._resetQuery()
        this._fetchQuery()
        // We're only updating the offset
      } else if (this.args.positional[2] && this.lastOffset < this.args.positional[2]) {
        this.lastOffset = this.args.positional[2]
        this._fetchQuery()
      }
    }
  }

  private _resetQuery() {
    this.query.unsubscribeToQuery(this.generatedQuery.queryInstanceId)
    this._generatedQuery = null
  }

  teardown() {
    this.query.unsubscribeToQuery(this.generatedQuery.queryInstanceId)
  }
}
